import { Skeleton } from 'components/Skeleton/Skeleton'
import cx from 'classnames'
import { FileType } from 'components/ReviewsWizard/FileSelection/urlValidationUtils'
import { useAuth } from 'hooks/UseAuth'
import { isDocumentHidden } from 'utils'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ArticleFlat, RootState } from 'types'
import { ARTICLE_VIEW_UPDATE_INTERVAL, REAL_TIME_WS_CHANNEL } from '../RealtimeProvider/utils'
import { ExternalResourceEmptyState } from './ExternalResourceEmptyState'
import { ExternalResourceLoadingState } from './ExternalResourceLoadingState'
import Styles from './ExternalResourceRenderer.module.scss'

type Props = {
  article?: ArticleFlat
  externalLink?: string
  customClass?: string
  title?: string
  icon?: ReactNode
  isPreview?: boolean
  showEmptyState?: boolean
  showLoadingState?: boolean
  fileType?: FileType
}

export const ExternalResourceRenderer = ({
  article,
  externalLink,
  customClass,
  title,
  icon,
  isPreview = false,
  showEmptyState = false,
  showLoadingState = false,
  fileType,
}: Props) => {
  const [loading, setLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const inHandbook = useSelector((state: RootState) => state.handbook)
  const websocketConnection = useWebsocketConnection()
  const auth = useAuth()

  useEffect(() => {
    if (auth && article) {
      const realTimeSubscription = websocketConnection?.subscriptions.create({
        channel: REAL_TIME_WS_CHANNEL,
        article_id: article.id,
      })
      realTimeSubscription?.perform('update_last_viewed_at', { layer_id: article.main_layer_id })
      const articleViewUpdateIntervalId = setInterval(() => {
        if (!isDocumentHidden()) {
          realTimeSubscription?.perform('update_last_viewed_at', { layer_id: article.main_layer_id })
        }
      }, ARTICLE_VIEW_UPDATE_INTERVAL)

      return () => {
        clearInterval(articleViewUpdateIntervalId)
        realTimeSubscription?.unsubscribe()
      }
    }
  }, [auth, article]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true)
  }, [externalLink])

  return (
    <div
      className={cx(Styles.iframeContainer, customClass, {
        [Styles.isPreview]: isPreview,
        [Styles.inHandbook]: inHandbook,
        [Styles.isGoogleDocs]: fileType === FileType.DOCUMENT,
        [Styles.isGoogleSheets]: fileType === FileType.SPREADSHEETS,
        [Styles.isAirtable]: fileType === FileType.AIRTABLE,
      })}
    >
      <iframe
        title="external-resource"
        onLoad={() => setLoading(false)}
        src={article?.external_link || externalLink}
        ref={iframeRef}
        loading="lazy"
      />
      {showEmptyState && <ExternalResourceEmptyState />}
      {loading && showLoadingState && <ExternalResourceLoadingState />}
      {isPreview && (
        <div className={Styles.iframeFooter}>
          <div className={Styles.icon}>{icon}</div>
          <div className={Styles.info}>
            {loading && !title ? (
              <Skeleton active paragraph={{ rows: 1, width: '100%' }} title={false} />
            ) : (
              <div className={Styles.title}>{title}</div>
            )}
            <div className={Styles.link}>{externalLink}</div>
          </div>
        </div>
      )}
    </div>
  )
}
